import { useState, useEffect, useCallback } from 'react'
import {
  Button,
  Checkbox,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Paper,
  Container,
  Divider,
  Alert,
  Loader,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertCircle, IconCheck, IconUserPlus, IconX } from '@tabler/icons-react'
import {
  InviteRequest,
  isOperationOutcome,
  normalizeErrorString,
  normalizeOperationOutcome,
} from '@medplum/core'
import type {
  OperationOutcome,
  Project,
  ProjectMembership,
  PractitionerRole as FHIRPractitionerRole,
} from '@medplum/fhirtypes'
import { Form, getErrorsForInput, useMedplum } from '@medplum/react'
import { SNOMED_USER_ROLES } from '@/utils'

export function InviteCallCenterAgent({ onClose }: { onClose: () => void }) {
  const medplum = useMedplum()
  const profile = medplum.getProfile()
  const [loading, setLoading] = useState<boolean>(true)
  const [project, setProject] = useState<Project | undefined>()
  const [outcome, setOutcome] = useState<OperationOutcome>()
  const [emailSent, setEmailSent] = useState(false)
  const [result, setResult] = useState<ProjectMembership | undefined>(undefined)

  // Load organization details and access policies
  const getProject = async () => {
    setLoading(true)
    try {
      if (profile && profile.meta && profile.meta.project) {
        const orgProject = await medplum.searchOne('Project', 'name:contains=behuman')
        setProject(orgProject)
      }
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `Failed to load project details: ${normalizeErrorString(error)}`,
        icon: <IconAlertCircle />,
      })
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    getProject()
  }, [medplum])

  const handleSubmit = useCallback(
    async (formData: Record<string, string>) => {
      try {
        if (!project) {
          throw new Error('Project not found')
        }

        // Make sure we have the call-center-manager policy
        const policy = await medplum.searchOne('AccessPolicy', 'name=call-center-agent-access')
        if (!policy) {
          throw new Error('Failed to retrieve practitioner access policy')
        }

        // Prepare invitation request
        const inviteBody: InviteRequest = {
          resourceType: 'Practitioner',
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          sendEmail: formData.sendEmail === 'on',
          mfaRequired: true,
          admin: false,
        }
        // Add organization reference for the resource being created
        const projectId = project.id as string
        const response = await medplum.invite(projectId, inviteBody)

        // Add the newly created resource to the project
        if (!isOperationOutcome(response)) {
          // The resource reference depends on the resource type
          const resourceType = inviteBody.resourceType
          const resourceId = response.profile.reference?.split('/')[1]

          if (resourceId) {
            // Update the resource to associate it with the organization
            const practitioner = await medplum.readResource(resourceType, resourceId)

            if (resourceType === 'Practitioner') {
              const practitionerAccounts = [
                {
                  reference: `Practitioner/${resourceId}`,
                },
                ...(practitioner.meta?.accounts || []),
              ]

              await medplum.post(
                `/fhir/R4/${practitioner.resourceType}/${practitioner.id}/$set-accounts`,
                {
                  resourceType: 'Parameters',
                  parameter: [
                    ...practitionerAccounts.map((item) => ({
                      name: 'accounts',
                      valueReference: item,
                    })),
                  ],
                },
              )

              const projectMembership = await medplum.searchOne(
                'ProjectMembership',
                `profile=Practitioner/${resourceId}&project=Project/${projectId}`,
              )
              if (!projectMembership) {
                throw new Error(`Failed to find project membership for Practitioner/${resourceId}`)
              }
              // For practitioners, we need to create a PractitionerRole
              // that links the practitioner to the organization

              const practitionerRole = await medplum.createResource<FHIRPractitionerRole>({
                resourceType: 'PractitionerRole',
                meta: {
                  project: projectId,
                },
                code: [
                  {
                    coding: [
                      {
                        system: SNOMED_USER_ROLES.CC_MANAGER.url,
                        code: SNOMED_USER_ROLES.CC_MANAGER.code,
                        display: SNOMED_USER_ROLES.CC_MANAGER.name,
                      },
                    ],
                  },
                ],
                practitioner: {
                  reference: `Practitioner/${resourceId}`,
                },
                active: true,
              })

              const accounts = [
                {
                  reference: `Practitioner/${resourceId}`,
                },
                ...(practitionerRole.meta?.accounts || []),
              ]

              await medplum.post(
                `/fhir/R4/${practitionerRole.resourceType}/${practitionerRole.id}/$set-accounts`,
                {
                  resourceType: 'Parameters',
                  parameter: [
                    ...accounts.map((item) => ({
                      name: 'accounts',
                      valueReference: item,
                    })),
                  ],
                },
              )
            }
          }

          // Update states to show success
          medplum.invalidateSearches('Patient')
          medplum.invalidateSearches('Practitioner')
          medplum.invalidateSearches('RelatedPerson')
          medplum.invalidateSearches('ProjectMembership')

          setResult(response)
          setEmailSent(inviteBody.sendEmail ?? false)

          notifications.show({
            color: 'green',
            title: 'Invitation Sent',
            message: `Successfully invited ${formData.firstName} ${formData.lastName} to the organization`,
            icon: <IconCheck />,
          })
        } else {
          setOutcome(response)
          throw new Error(`Invitation failed: ${normalizeOperationOutcome(response)}`)
        }
      } catch (err) {
        notifications.show({
          color: 'red',
          title: 'Invitation Failed',
          message: normalizeErrorString(err),
          icon: <IconAlertCircle />,
        })
        setOutcome(normalizeOperationOutcome(err))
      }
    },
    [medplum, project],
  )

  if (loading) {
    return (
      <Container size="md" py="xs">
        <Stack align="center">
          <Loader size="lg" />
          <Text>Loading organization details...</Text>
        </Stack>
      </Container>
    )
  }
  return (
    <Container>
      <Paper>
        {result ? (
          // Success message after in vitation
          <Stack>
            <div className="flex justify-between align-middle">
              <Title order={2}>Invitation Sent</Title>
              <Button variant="transparent" size="xs" onClick={() => onClose()}>
                <IconX size={22} color="black" />
              </Button>
            </div>
            <Alert color="green" title="Member successfully invited">
              {emailSent ? (
                <Text>
                  An email invitation has been sent to the new member with instructions to complete
                  the registration process.
                </Text>
              ) : (
                <Text>
                  The member has been added as Call Center Manager. No email notification was sent.
                </Text>
              )}
            </Alert>
            <Text size="sm">
              Role: <strong>Call Center Manager</strong>
            </Text>
            <Button onClick={() => setResult(undefined)}>Invite Another Member</Button>
          </Stack>
        ) : (
          // Invitation form
          <Form onSubmit={handleSubmit} style={{ padding: '16px 0px' }}>
            <Stack>
              <div className="flex justify-between align-middle">
                <Text>Invite a new call center agent.</Text>
                <Button variant="transparent" size="xs">
                  <IconX size={22} onClick={() => onClose()} color="#000" />
                </Button>
              </div>

              <Divider />
              <Stack py="md">
                <TextInput
                  name="firstName"
                  label="First Name"
                  placeholder="Enter first name"
                  required
                  error={getErrorsForInput(outcome, 'firstName')}
                />
                <TextInput
                  name="lastName"
                  label="Last Name"
                  placeholder="Enter last name"
                  required
                  error={getErrorsForInput(outcome, 'lastName')}
                />
                <TextInput
                  name="email"
                  label="Email Address"
                  placeholder="Enter email address"
                  required
                  type="email"
                  error={getErrorsForInput(outcome, 'email')}
                />
                <Checkbox
                  name="sendEmail"
                  label="Send invitation email"
                  description="An email will be sent with instructions to complete registration"
                  defaultChecked
                />
              </Stack>
              <Divider />
              <Group mt="md">
                <Button type="submit">
                  <IconUserPlus size={16} /> Send Invitation
                </Button>
              </Group>
            </Stack>
          </Form>
        )}
      </Paper>
    </Container>
  )
}

export default InviteCallCenterAgent
